import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Shift : famille "créneau de travail planifié".
 *
 * Créneau planifié qui affecte un intervenant à un poste, un site ou une
 * tournée sur une plage horaire. Construite sur le modèle "famille + type" de
 * la bible (patron : `asset`).
 *
 * Le socle ne connaît pas la nature métier concrète du créneau : il connaît la
 * famille générique `shift`. Le vocabulaire est porté par le champ `type`
 * (chaîne libre déclarée par l'app : "poste", "tournee", "astreinte"...), et
 * l'état du cycle de vie (proposé, confirmé, effectué, remplacé) par `status`.
 *
 * Références vers d'autres familles = de simples ids (aucun import de schéma) :
 * `workerId` (l'intervenant affecté) est le CŒUR de la famille, donc requis. Le
 * site `placeId`, la tournée `routeId`, l'absence liée `absenceId` et la
 * disponibilité `availabilityId` sont des liens optionnels. Le lien vers la
 * `timesheet` ne vit PAS ici : c'est la timesheet qui porte un `shiftId`.
 *
 * Sécurité : la fiche ne signale rien de sensible, la famille reste org-wide
 * (même doctrine que asset) ; l'accès fin (chaque worker ne voit que ses
 * créneaux) viendra après v0.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille shift (hors champs standard du socle). */
export const shiftCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "poste", "tournee", "astreinte"). */
  type: z.string().min(1),
  /** Intervenant affecté au créneau (référence `worker`, cœur de la famille). Requis. */
  workerId: z.string().min(1),
  /** Début de la plage horaire. Optionnel. */
  startAt: z.date().optional(),
  /** Fin de la plage horaire. Optionnel. */
  endAt: z.date().optional(),
  /** Poste / rôle tenu sur le créneau, libellé libre (ex. "Accueil", "Caisse"). Optionnel. */
  roleLabel: z.string().min(1).optional(),
  /** Site / lieu du créneau (référence `place`). Optionnel. */
  placeId: z.string().min(1).optional(),
  /** État métier, chaîne libre pilotée par l'app (ex. "propose", "confirme", "effectue", "remplace"). Optionnel. */
  status: z.string().min(1).optional(),
  /** Tournée couverte par le créneau (référence `route`). Optionnel. */
  routeId: z.string().min(1).optional(),
  /** Absence liée au créneau, ex. cause d'un remplacement (référence `absence`). Optionnel. */
  absenceId: z.string().min(1).optional(),
  /** Disponibilité dont le créneau est issu (référence `availability`). Optionnel. */
  availabilityId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille shift (champs standard + champs métier). */
export const ShiftSchema = z.object({
  ...baseEntityFields,
  ...shiftCoreFields,
});

/** Type d'un shift. */
export type Shift = z.infer<typeof ShiftSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const ShiftCreateInputSchema = z.object({
  ...shiftCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un shift. */
export type ShiftCreateInput = z.infer<typeof ShiftCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const ShiftUpdateInputSchema = z
  .object({ ...shiftCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'un shift. */
export type ShiftUpdateInput = z.infer<typeof ShiftUpdateInputSchema>;
